import axios, { AxiosInstance, AxiosResponse } from 'axios'

import { SentryUtils } from '../../utils/SentryUtils'
import { HttpClient } from '../BaseRepository'
import { BaseHttpClient, GenericResponse } from './BaseHttpClient'

type RequestWithoutData = (endpoint: string, config: object) => Promise<AxiosResponse>
type RequestWithData = (endpoint: string, data: any, config: object) => Promise<AxiosResponse>

/**
 * Http client implementation using the AXIOS http client library.
 */
export class AxiosHttpClient extends BaseHttpClient {
  private axiosInstance?: AxiosInstance

  constructor() {
    super(HttpClient.AXIOS)
  }

  /**
   * Configures the Axios HTTP client with axios.
   * @param baseUrl The base API url.
   * @param timeout The default request timeout in milliseconds.
   * @return BaseHttpClient An instance of AxiosHttpClient.
   */
  public configure = (baseUrl: string, timeout: number): BaseHttpClient => {
    this.axiosInstance = axios.create({
      baseURL: baseUrl,
      timeout: timeout,
      validateStatus: undefined,
    })

    // Add a response interceptor to handle all 401 and redirects to /users/signin if the user is unauthorized.
    this.axiosInstance.interceptors.response.use((response: AxiosResponse) => {
      if (response.status === 401 || (response.status === 404 && response.request?.responseURL.indexOf('/users/sign_in') > -1)) {
        window.onbeforeunload = null
        window.location.href = '/users/sign_in'
      }
      return response
    })

    return this
  }

  public isConfigured = (): boolean => {
    return this.axiosInstance != undefined
  }

  public getImpl = (endpoint: string, data: any, headers: object): Promise<GenericResponse> => {
    return this.handleRequestNoData(endpoint, headers, SentryUtils.expect<AxiosInstance>(this.axiosInstance).get)
  }

  public postImpl = (endpoint: string, data: any, headers: object): Promise<GenericResponse> => {
    return this.handleRequestWithData(endpoint, data, headers, SentryUtils.expect<AxiosInstance>(this.axiosInstance).post)
  }

  public putImpl = (endpoint: string, data: any, headers: object): Promise<GenericResponse> => {
    return this.handleRequestWithData(endpoint, data, headers, SentryUtils.expect<AxiosInstance>(this.axiosInstance).put)
  }

  public patchImpl = (endpoint: string, data: any, headers: object): Promise<GenericResponse> => {
    return this.handleRequestWithData(endpoint, data, headers, SentryUtils.expect<AxiosInstance>(this.axiosInstance).patch)
  }

  public deleteImpl = (endpoint: string, data: any, headers: object): Promise<GenericResponse> => {
    return this.handleRequestNoData(endpoint, headers, SentryUtils.expect<AxiosInstance>(this.axiosInstance).delete)
  }

  private handleRequestWithData = (endpoint: string, data: any, headers: object, func: RequestWithData): Promise<GenericResponse> => {
    return new Promise((resolve, reject) => {
      const { signal, ...headersWithoutSignal } = (headers ?? {}) as Record<string, any> & { signal?: AbortSignal }
      func(endpoint, data, { headers: headersWithoutSignal, ...(signal && { signal }) })
        .then((response) => {
          const httpClientResponse: GenericResponse = {
            data: response.data,
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
          }
          resolve(httpClientResponse)
        })
        .catch((error) => {
          reject(error)
        })
    })
  }

  private handleRequestNoData = (endpoint: string, headers: object, func: RequestWithoutData): Promise<GenericResponse> => {
    return new Promise((resolve, reject) => {
      func(endpoint, { headers: headers })
        .then((response) => {
          const httpClientResponse: GenericResponse = {
            data: response.data,
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
          }
          resolve(httpClientResponse)
        })
        .catch((error) => {
          reject(error)
        })
    })
  }
}
